import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "./SignUp.css";
import logo from "../logo.jpg";
import { Link, useNavigate } from "react-router-dom";
import { authApi } from "../../api/client";
import { getApiErrorMessage } from "../../api/http";
import { useAuth } from "../../context/AuthContext";


const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  phone: yup.string().required("Phone is required").matches(/^[0-9]+$/, "Digits only"),
  email: yup.string().email("Invalid email").required(),
  dob: yup.string().required("Required"),
  gender: yup.string().required("Required"),
  password: yup.string().min(8, "8+ characters").required(),
  confirmPassword: yup.string().oneOf([yup.ref("password")], "Passwords must match")
});

const SignUp = () => {
  const [role, setRole] = useState("student");
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setAuthSession } = useAuth();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const onSubmit = async (values) => {
    setApiError("");

    if (role === "instructor") {
      setApiError("Instructor accounts are created by admin in current backend.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        fullName: values.name,
        phone: values.phone,
        email: values.email,
        dateOfBirth: values.dob,
        gender: values.gender,
        password: values.password,
      };
      const { data } = await authApi.register(payload);
      setAuthSession(data.token, data.user);
      navigate("/welcome");
    } catch (err) {
      setApiError(getApiErrorMessage(err, "Failed to create account"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="container">
        <div className="row align-items-center">

          <div className="col-lg-6 text-white brand-section pe-lg-5 d-none d-lg-block my-5">
            <div className="d-flex align-items-center mb-4">
      <div className="logo-placeholder me-4">
                <img src={logo} className='rounded-circle' alt="IEEE Logo" width="75" />
              </div>              <h3 className="m-0 fw-bold">IEEE Study Groups</h3>
            </div>
            <h1 className="fw-bold mb-4">Expand your <br/> academic horizon.</h1>
            <p className="lead opacity-75 mb-4">
              Whether you are here to discover or to improve. IEEE Study Groups bridges the gap between you and real experience...
            </p>
            <p className="fw-bold">Start The Journey now !</p>
          </div>

          {/* Right Side Form */}
          <div className="col-lg-5 offset-lg-1 p-5">
            <div className="card auth-card p-4 p-md-5 ">
              <div className="text-center mb-4">
                <h2 className="fw-bold">Create your account</h2>
                <p className="text-muted small">Already a member? <Link to="/" className="text-decoration-none fw-bold">Log in</Link></p>
              </div>

              <div className="role-switcher d-flex mb-4">
                <button type="button" className={`role-btn flex-fill ${role === 'student' ? 'active' : ''}`} onClick={() => setRole('student')}>Student</button>
                <button type="button" className={`role-btn flex-fill ${role === 'instructor' ? 'active' : ''}`} onClick={() => setRole('instructor')}>Instructor</button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input {...register("name")} className="form-control custom-input" placeholder="Ex: Thomas farag" />
                  {errors.name ? <small className="text-danger">{errors.name.message}</small> : null}
                </div>

                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <input {...register("phone")} className="form-control custom-input" placeholder="01006969123" />
                  {errors.phone ? <small className="text-danger">{errors.phone.message}</small> : null}
                </div>

                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input {...register("email")} className="form-control custom-input" placeholder="user@gmail.com" />
                  {errors.email ? <small className="text-danger">{errors.email.message}</small> : null}
                </div>

                <div className="row mb-3">
                  <div className="col-7">
                    <label className="form-label">Date of Birth</label>
                    <input type="date" {...register("dob")} className="form-control custom-input" />
                    {errors.dob ? <small className="text-danger">{errors.dob.message}</small> : null}
                  </div>
                  <div className="col-5">
                    <label className="form-label">Gender</label>
                    <select {...register("gender")} className="form-select custom-input">
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                    {errors.gender ? <small className="text-danger">{errors.gender.message}</small> : null}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Password</label>
                  <input type="password" {...register("password")} className="form-control custom-input" placeholder="Enter Your Password" />
                  {errors.password ? <small className="text-danger">{errors.password.message}</small> : null}
                </div>

                <div className="mb-3">
                  <label className="form-label">Confirm Password</label>
                  <input type="password" {...register("confirmPassword")} className="form-control custom-input" placeholder="Re-Enter Your Password" />
                  {errors.confirmPassword ? <small className="text-danger">{errors.confirmPassword.message}</small> : null}
                </div>

                {apiError ? <div className="alert alert-danger py-2 small">{apiError}</div> : null}
                <button type="submit" disabled={loading} className="btn btn-primary w-100 py-2 fw-bold mt-3 btn-signin">
                  {loading ? "Creating account..." : "Sign Up"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
